/**
 * Empty state for documents - shows "+ Add" button to create first section
 * Story 227: Edit PRDs, tech designs and tasks
 * Story 239: Auto-generate anchors for coverage linkages
 * Story 297: Widened to support generic document types
 */

import { useCallback } from 'react';
import { useDocumentMutations } from '../../hooks/useDocumentMutations';
import { generateAnchorFromTitle } from '../../utils/document';
import './EmptyDocState.css';

/** Fixed doc types that use anchor auto-generation */
const LINKED_DOC_TYPES = new Set(['prd', 'design']);

interface EmptyDocStateProps {
  projectSlug: string;
  storyId: string;
  /** Story 297: Widened from 'prd' | 'design' to string for generic doc support */
  docType: string;
}

const DOC_TYPE_LABELS: Record<string, string> = {
  prd: 'PRD',
  design: 'Tech Design',
};

/** Default title for first section */
const FIRST_SECTION_TITLE = 'Overview';
/** Sequence number for first section */
const FIRST_SECTION_SEQ = 1;

export function EmptyDocState({ projectSlug, storyId, docType }: EmptyDocStateProps) {
  const { createSection } = useDocumentMutations(projectSlug, storyId, docType);
  const label = DOC_TYPE_LABELS[docType] ?? docType;

  const handleAdd = useCallback(() => {
    // Story 297: Only auto-generate anchors for linked doc types (prd/design)
    const autoAnchor = LINKED_DOC_TYPES.has(docType)
      ? generateAnchorFromTitle(FIRST_SECTION_TITLE, FIRST_SECTION_SEQ)
      : null;
    createSection.mutate({
      title: FIRST_SECTION_TITLE,
      body: '',
      section_key: 'section-1',
      anchor: autoAnchor,
    });
  }, [createSection, docType]);

  return (
    <button
      className="empty-doc-state"
      onClick={handleAdd}
      disabled={createSection.isPending}
      type="button"
    >
      <svg viewBox="0 0 16 16" fill="currentColor" width="16" height="16">
        <path d="M7.75 2a.75.75 0 0 1 .75.75V7h4.25a.75.75 0 0 1 0 1.5H8.5v4.25a.75.75 0 0 1-1.5 0V8.5H2.75a.75.75 0 0 1 0-1.5H7V2.75A.75.75 0 0 1 7.75 2z" />
      </svg>
      {createSection.isPending ? 'Adding...' : `Add ${label}`}
    </button>
  );
}
